import { FC, HTMLAttributes, useEffect, useMemo, useState } from "react";
import { useStyles } from "./VoucherItemDetail.style";
import AppBar from "../AppBar/AppBar";
import { Icon } from "../../core/Icon/Icon";
import { HistoryGaming } from "../../../models/historyGaming";
import { useNavigate, useParams } from "react-router-dom";
import { StaticImage } from "../StaticImage";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { Button } from "../../core/Button/Button";
import { TabsList } from "../TabsList";
import { ActiveIframe } from "../GameFeed/ActiveIframe";
import { PageRoutes } from "../../../constants/historyGaming";
import { useItemsByItemId } from "../../../hooks/resolvers/shop/useShopQuery";
import { ItemCurrencySymbol, ItemDetailTab } from "../../../constants/voucher";
import { EpayInventoryCost, InventoryEpayItemData } from "@onmo/graphql/graphql";

export const VoucherItemDetail: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { bundleId, itemId } = useParams<{ bundleId: string; itemId: string }>();
  const [activeTab, setActiveTab] = useState(ItemDetailTab.TermsAndConditions);
  const [activeIframe, setActiveIframe] = useState<string | null>(null);
  const { item, loading } = useItemsByItemId(bundleId, itemId);

  const tabs = [
    ItemDetailTab.TermsAndConditions,
    ItemDetailTab.ProductInfo,
    ItemDetailTab.Redeem
  ];

  useEffect(() => {
    if (!loading && !item) {
      navigate(`${PageRoutes.Store}`);
    }
  }, [loading, item, navigate]);

  const { currency, amount } = useMemo(() => {
    return (item?.prices?.[0] as EpayInventoryCost) || { currency: "", amount: 0 };
  }, [item]);

  const handlePurchaseClick = () => {
    setActiveIframe((item?.data as InventoryEpayItemData).purchaseLink);
  };

  const handleCloseIframe = () => {
    setActiveIframe(null);
    console.log("Iframe closed");
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as ItemDetailTab);
  };

  const renderTabContent = () => {
    if (!item) return null;
    const itemData = item.data as InventoryEpayItemData;
    switch (activeTab) {
      case ItemDetailTab.TermsAndConditions:
        return itemData.termsAndConditions;
      case ItemDetailTab.ProductInfo:
        return itemData.description;
      case ItemDetailTab.Redeem:
        return itemData.redeemDescription;
      default:
        return null;
    }
  };

  return (
    <>
      {item && (
        <div className={classes.root}>
          <AppBar
            leftItems={
              <Icon
                name="icon-bold-arrow-left-3"
                onClick={() => HistoryGaming.back(navigate)}
                data-testid="back-icon"
              />
            }
          />
          <div className={classes.content}>
            <StaticImage
              src={(item.data as InventoryEpayItemData).url.replace("format=small", "format=original")}
              className={classes.itemImage}
            />

            <div>
              <div className={classes.headerSection}>
                <Typography variant={TypographyVariant.BodyB3Medium}>
                  {item.title}
                </Typography>

                {amount > 0 && (
                  <Typography className={classes.price} variant={TypographyVariant.BodyB3Regular}>
                    {ItemCurrencySymbol.get(currency)}
                    {amount}
                  </Typography>
                )}
              </div>
              <Typography variant={TypographyVariant.BodyB6Regular}>
                {(item.data as InventoryEpayItemData).shortDescription}
              </Typography>
            </div>

            <Button
              variant={"contained"}
              size="large"
              onClick={handlePurchaseClick}
              fullWidth>
              Purchase&nbsp;
              <Icon name="icon-linear-arrow-right" size={24} />
            </Button>

            <TabsList
              listTabs={tabs}
              activeTab={activeTab}
              onChangeTab={handleTabChange}
              tabVariant="underlined"
              className={classes.tabsList}
            />
            <div className={classes.tabContent}>
              {renderTabContent()?.split("\n")
                .map((line: string, index: number) => (
                  <Typography variant={TypographyVariant.BodyB6Regular}
                    key={index}>{line}
                  </Typography>
                ))}
            </div>
          </div>
          {
            activeIframe && (
              <ActiveIframe
                iframeUrl={activeIframe}
                onCloseIframe={handleCloseIframe}
                feedId={"feedId"}
              />
            )
          }
        </div>
      )}
    </>
  )
};